import { log } from './logger'

// Longest side images are kept at: a wallpaper card is at most ~250px on its
// long side at export size, and artist photos are 400px squares.
const MAX_SIDE = 400
const FETCH_TIMEOUT_MS = 8000

// Bitmaps by URL for the whole session: every redraw and every export reuses
// them, so a photo is downloaded and decoded once.
const bitmaps = new Map<string, Promise<ImageBitmap | null>>()

/**
 * A photo ready to draw into a canvas that can still be exported. The copy the
 * page already shows came without CORS and would taint the canvas, so this
 * downloads it again in CORS mode — once — and decodes it off the main thread
 * (createImageBitmap). Resolves null when it can't be had; the caller draws
 * its fallback.
 */
export function loadCanvasImage(url: string): Promise<ImageBitmap | null> {
  let entry = bitmaps.get(url)
  if (!entry) {
    entry = fetchBitmap(url).catch(err => {
      log('canvas-images', 'image failed', { url, error: String(err) })
      bitmaps.delete(url)
      return null
    })
    bitmaps.set(url, entry)
  }
  return entry
}

async function fetchBitmap(url: string): Promise<ImageBitmap> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  try {
    const res = await fetch(url, { mode: 'cors', cache: 'force-cache', signal: controller.signal })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const full = await createImageBitmap(await res.blob())
    const ratio = MAX_SIDE / Math.max(full.width, full.height)
    if (ratio >= 1) return full
    try {
      const small = await createImageBitmap(full, {
        resizeWidth: Math.round(full.width * ratio),
        resizeHeight: Math.round(full.height * ratio),
        resizeQuality: 'high',
      })
      full.close()
      return small
    } catch {
      // Older WebKit rejects the resize options; the full bitmap still draws.
      return full
    }
  } finally {
    clearTimeout(timer)
  }
}
